package com.arenaproplay.admin.ui.localization

import androidx.compose.runtime.staticCompositionLocalOf

interface AppStrings {
    val appName: String

    val loading: String
    val retry: String
    val dismiss: String
    val cancel: String
    val save: String
    val errorGeneric: String

    val themeLight: String
    val themeDark: String

    val navOverview: String
    val navUsers: String
    val navClubs: String
    val navFields: String
    val navBookings: String
    val navEvents: String
    val navTeams: String
    val navSubscriptions: String
    val navSocial: String
    val navChat: String
    val navAuditLog: String
    val navAdmins: String
    val navSettings: String

    val loginTitle: String
    val loginSubtitle: String
    val loginEmail: String
    val loginPassword: String
    val loginSubmit: String
    val loginErrorInvalid: String
    val loginErrorNotAdmin: String

    val logout: String

    val periodWeek: String
    val periodMonth: String
    val periodYear: String

    val kpiTotalUsers: String
    val kpiActiveUsers: String
    val kpiTotalRevenue: String
    val kpiTotalBookings: String

    val sectionUserGrowth: String
    val sectionRevenueBySport: String
    val sectionBookingStatus: String
    val sectionTopClubs: String
    val sectionRecentActivity: String
    val sectionBookingHeatmap: String
    val sectionSportPopularity: String

    val emptyData: String
    val comingSoon: String
}

object AppStringsEN : AppStrings {
    override val appName = "Arena Admin"

    override val loading = "Loading..."
    override val retry = "Retry"
    override val dismiss = "Dismiss"
    override val cancel = "Cancel"
    override val save = "Save"
    override val errorGeneric = "Something went wrong"

    override val themeLight = "Light"
    override val themeDark = "Dark"

    override val navOverview = "Overview"
    override val navUsers = "Users"
    override val navClubs = "Clubs"
    override val navFields = "Fields"
    override val navBookings = "Bookings"
    override val navEvents = "Events"
    override val navTeams = "Teams"
    override val navSubscriptions = "Subscriptions"
    override val navSocial = "Social"
    override val navChat = "Chat"
    override val navAuditLog = "Audit log"
    override val navAdmins = "Admins"
    override val navSettings = "Settings"

    override val loginTitle = "Sign in to Arena Admin"
    override val loginSubtitle = "Use your admin account to continue"
    override val loginEmail = "Email"
    override val loginPassword = "Password"
    override val loginSubmit = "Sign in"
    override val loginErrorInvalid = "Invalid email or password"
    override val loginErrorNotAdmin = "This account does not have admin access"

    override val logout = "Sign out"

    override val periodWeek = "Week"
    override val periodMonth = "Month"
    override val periodYear = "Year"

    override val kpiTotalUsers = "Total users"
    override val kpiActiveUsers = "Active users"
    override val kpiTotalRevenue = "Total revenue"
    override val kpiTotalBookings = "Total bookings"

    override val sectionUserGrowth = "User growth"
    override val sectionRevenueBySport = "Revenue by sport"
    override val sectionBookingStatus = "Booking status"
    override val sectionTopClubs = "Top clubs"
    override val sectionRecentActivity = "Recent activity"
    override val sectionBookingHeatmap = "Bookings by hour & day"
    override val sectionSportPopularity = "Sport popularity"

    override val emptyData = "No data"
    override val comingSoon = "Coming soon"
}

object AppStringsUA : AppStrings {
    override val appName = "Arena Admin"

    override val loading = "Завантаження..."
    override val retry = "Повторити"
    override val dismiss = "Скасувати"
    override val cancel = "Скасувати"
    override val save = "Зберегти"
    override val errorGeneric = "Щось пішло не так"

    override val themeLight = "Світла"
    override val themeDark = "Темна"

    override val navOverview = "Огляд"
    override val navUsers = "Користувачі"
    override val navClubs = "Клуби"
    override val navFields = "Поля"
    override val navBookings = "Бронювання"
    override val navEvents = "Події"
    override val navTeams = "Команди"
    override val navSubscriptions = "Підписки"
    override val navSocial = "Соціальне"
    override val navChat = "Чат"
    override val navAuditLog = "Журнал дій"
    override val navAdmins = "Адміни"
    override val navSettings = "Налаштування"

    override val loginTitle = "Вхід у Arena Admin"
    override val loginSubtitle = "Використайте адмін-акаунт для продовження"
    override val loginEmail = "Email"
    override val loginPassword = "Пароль"
    override val loginSubmit = "Увійти"
    override val loginErrorInvalid = "Невірний email або пароль"
    override val loginErrorNotAdmin = "Цей акаунт не має адмін-доступу"

    override val logout = "Вийти"

    override val periodWeek = "Тиждень"
    override val periodMonth = "Місяць"
    override val periodYear = "Рік"

    override val kpiTotalUsers = "Всього користувачів"
    override val kpiActiveUsers = "Активні користувачі"
    override val kpiTotalRevenue = "Загальний дохід"
    override val kpiTotalBookings = "Всього бронювань"

    override val sectionUserGrowth = "Зростання користувачів"
    override val sectionRevenueBySport = "Дохід за видом спорту"
    override val sectionBookingStatus = "Статус бронювань"
    override val sectionTopClubs = "Топ клуби"
    override val sectionRecentActivity = "Остання активність"
    override val sectionBookingHeatmap = "Бронювання за годинами"
    override val sectionSportPopularity = "Популярність видів спорту"

    override val emptyData = "Немає даних"
    override val comingSoon = "Скоро"
}

enum class Language(val code: String, val strings: AppStrings) {
    ENGLISH("en", AppStringsEN),
    UKRAINIAN("ua", AppStringsUA);

    companion object {
        fun fromCode(code: String?): Language {
            return entries.find { it.code == code } ?: ENGLISH
        }
    }
}

val LocalAppStrings = staticCompositionLocalOf<AppStrings> { AppStringsEN }
